import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import {
  adminUpdateCourseHoursRange,
  getCourseHoursRangeAsAdmin,
} from './adminApi';
import { getCourseHoursRange } from './api';
import { RootState } from '../..';

export type TCourseHoursRangeType = 'normal' | 'trial';

export interface ICourseHoursRangeItem {
  _id?: string;
  minValue: number;
  maxValue: number;
}

export type ICourseHoursRange = Partial<
  Record<TCourseHoursRangeType, ICourseHoursRangeItem>
>;

export const COURSE_HOURS_RANGE_TYPES: readonly TCourseHoursRangeType[] = [
  'normal',
  'trial',
] as const;

export const COURSE_HOURS_RANGE_LABELS: Record<
  TCourseHoursRangeType,
  string
> = {
  normal: 'Normal',
  trial: 'Trial',
};

export interface IInitialCourseHoursRangeState {
  status: 'idle' | 'loading' | 'rejected';
  adminCourseHoursRange?: ICourseHoursRange;
  courseHoursRange?: ICourseHoursRangeItem;
}

const initialState: IInitialCourseHoursRangeState = {
  status: 'idle',
  adminCourseHoursRange: undefined,
  courseHoursRange: undefined,
};

export const CourseHoursRangeState = createSlice({
  name: 'courseHoursRange',
  initialState,
  reducers: {
    resetAdminCourseHoursRangesData: (state) => {
      state.adminCourseHoursRange = undefined;
    },
    resetCourseHoursRange: (state) => {
      state.courseHoursRange = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCourseHoursRangeAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        state.adminCourseHoursRange = action.payload;
      })
      .addCase(adminUpdateCourseHoursRange.fulfilled, (state, action) => {
        state.status = 'idle';

        state.adminCourseHoursRange = action.payload;
      })
      .addCase(getCourseHoursRange.fulfilled, (state, action) => {
        state.status = 'idle';
        state.courseHoursRange = action.payload;
      });

    builder.addMatcher(
      isPending(
        getCourseHoursRangeAsAdmin,
        adminUpdateCourseHoursRange,
        getCourseHoursRange
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        getCourseHoursRangeAsAdmin,
        adminUpdateCourseHoursRange,
        getCourseHoursRange
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const {
  resetAdminCourseHoursRangesData,
  resetCourseHoursRange,
} = CourseHoursRangeState.actions;

export const getCourseHoursRangeState = (
  state: RootState
): RootState['courseHoursRange'] => state.courseHoursRange;

export * from './adminApi';
export * from './api';
export default CourseHoursRangeState.reducer;
